import { DropdownPassThroughMethodOptions } from "primereact/dropdown";
import {
  PaginatorContext,
  PaginatorPassThroughMethodOptions,
  PaginatorPassThroughOptions,
} from "primereact/paginator";
import { classNames } from "primereact/utils";

const paginatorButton = ({ context }: { context: PaginatorContext }) => {
  return {
    className: classNames(
      "relative inline-flex items-center justify-center user-none overflow-hidden leading-none",
      "border-0 text-gray-500 p-2 rounded-md",
      "transition duration-200",
      "dark:text-white", //Dark Mode
      {
        "cursor-default pointer-events-none opacity-60": context.disabled,
        "focus:outline-none focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)]":
          !context.disabled, // Focus
      },
    ),
  };
};

export const paginator: Partial<PaginatorPassThroughOptions> = {
  root: {
    className: classNames(
      "flex items-center justify-end flex-wrap text-sm bg-transparent",
      "text-gray-500 border-0 px-1 rounded-md mt-3 pb-3",
      "dark:bg-gray-900 dark:text-white/60 dark:border-blue-900/40", // Dark Mode
    ),
  },
  firstPageButton: paginatorButton,
  prevPageButton: paginatorButton,
  nextPageButton: paginatorButton,
  lastPageButton: paginatorButton,
  pageButton: ({ context }: PaginatorPassThroughMethodOptions) => ({
    className: classNames(
      "relative inline-flex items-center justify-center user-none overflow-hidden leading-none",
      "border-0 text-gray-500 m-1 rounded-md px-2 h-min",
      "transition duration-200",
      "dark:border-blue-300 dark:text-white", // Dark Mode
      "outline-none focus:outline-none focus:outline-offset-0", // Focus
      {
        "font-bold bg-white text-blue-700": context.active,
      },
    ),
  }),
  current: () => ({
    className: "mr-2 text-sm text-gray-800 flex-1 justify-start cursor-default",
  }),
  RPPDropdown: {
    root: ({ props, state }: DropdownPassThroughMethodOptions) => {
      return {
        className: classNames(
          "inline-flex relative cursor-pointer user-none w-min md:w-min",
          "bg-white border rounded-md",
          "transition duration-200",
          "ml-2 w-fit h-[30px] leading-[30px]",
          "dark:bg-gray-950 dark:border-blue-900/40", //DarkMode
          {
            "outline-none outline-offset-0 shadow-[0_0_0_0.2rem_rgba(191,219,254,1)] border-blue-500":
              state.focused && !props.disabled, //Focus
            "border-gray-300": !state.focused,
            "hover:border-blue-500": !props.disabled, //Hover
          },
        ),
      };
    },
    input: {
      className: classNames(
        "font-sans text-base text-gray-600 p-0 px-1 m-0 rounded-md apperance-none text-sm w-fit",
        "block whitespace-nowrap overflow-hidden w-[1%] cursor-pointer text-ellipsis border-0 pr-0",
        "focus:outline-none focus:outline-offset-0",
        "dark:text-white", //Dark Mode
      ),
    },
    trigger: {
      className: classNames(
        "flex items-center justify-center shrink-0",
        "text-gray-500 dark:text-white w-12 rounded-r-md",
      ),
    },
    panel: {
      className: classNames(
        "bg-white text-gray-600 border-0 rounded-md shadow-[0_2px_12px_rgba(0,0,0,0.1)]",
        "dark:bg-gray-900 dark:text-white/80 dark:border-blue-900/40", //Dark Mode
      ),
    },
    wrapper: {
      className: "overflow-auto w-fit",
    },
    list: { className: "m-0 p-0 py-1 list-none" },
    item: ({ context }: DropdownPassThroughMethodOptions) => ({
      className: classNames(
        "relative font-normal cursor-pointer space-nowrap overflow-hidden text-sm",
        "m-0 py-1 px-5 border-none text-gray-600 rounded-none",
        "transition duration-200",
        "dark:text-white/80", // Dark Mode
        {
          "text-blue-700 bg-blue-50 dark:text-white/80 dark:bg-blue-300":
            !context.focused && context.selected,
          "bg-blue-300/40": context.focused && context.selected,
          "text-gray-600 bg-gray-300 dark:text-white/80 dark:bg-blue-900/40":
            context.focused && !context.selected,
        },
      ),
    }),
  },
};
